import {
	CountryEnum,
	PartnerAddressType,
	type PartnerResponseDto,
	PartnerSkills,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { useCreatePartner } from "Partner/Api/Mutation/createPartnerMutation";
import {
	getCreatePartnerFormModelSchema,
	type PartnerFormModel,
} from "Partner/Components/Form/Schema/partnerFormModelSchema";
import { PartnerPaths } from "Partner/Routes/Routing";
import { useVerticalListQuery } from "Settings/Modules/OrderSetup/Api/Query/useVerticalListQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

const partnerCreateResources = Resources.base.partner.detail;
const selectOptionsResource = Resources.base.partner.selectOptions;

export const PartnerCreateForm = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { market } = useMarket();
	const { data: verticals, isLoading: isLoadingVerticals } =
		useVerticalListQuery();

	const formDefaultValues = {
		isSameAddress: true,
		partner: {
			name: "",
			middleName: "",
			surname: "",
			isCanTrainDay: false,
			marketId: market?.id,
			marketRegionId: undefined,
			phone: "",
			email: "",
			teamSize: 1,
			verticals: [],
			skills: [],
			notes: "",
		},
		address: {
			street: "",
			city: "",
			buildingNumber: "",
			postCode: "",
			country: CountryEnum.CZE,
			type: PartnerAddressType.PRIMARY,
		},
		billingProfile: {
			name: "",
			vatId: "",
			vatLiable: false,
			businessId: "",
			type: "individual",
			tin: "",
			birthCountry: "",
			birthCity: "",
			birthDate: "",
		},
		billingAddress: {
			street: "",
			city: "",
			buildingNumber: "",
			postCode: "",
			country: null,
			type: "individual",
		},
		bankAccount: {
			iban: "",
			bic: "",
			accountHolder: "",
		},
	};

	const form = useForm<PartnerFormModel>({
		resolver: zodResolver(getCreatePartnerFormModelSchema(t)),
		defaultValues: formDefaultValues,
		reValidateMode: "onChange",
		mode: "onChange",
	});

	const {
		watch,
		control,
		setValue,
		formState: { errors },
	} = form;

	const selectedMarketId = watch("partner.marketId");
	const selectedType = watch("billingProfile.type");
	const selectedSameAddress = watch("isSameAddress");

	const { data: marketList } = useMarketListQuery();
	const {
		data: marketRegionList,
		refetch: refetchMarketRegions,
		isLoading: isLoadingMarketRegions,
	} = useMarketRegionList(selectedMarketId);

	const { handleServerError } = useHandleServerError(form);
	// biome-ignore lint: must check the types
	const createPartner: any = useCreatePartner().mutate;

	useEffect(() => {
		if (market && marketList) {
			setValue("partner.marketId", market.id);
		}
	}, [market, marketList]);

	useEffect(() => {
		selectedMarketId && refetchMarketRegions();
	}, [selectedMarketId]);

	const handleSubmitCreate = () => {
		handleServerError(createPartner, {
			onSuccess: (data) => {
				form.reset(formDefaultValues);
				navigate(
					PartnerPaths.Detail.path((data as PartnerResponseDto).partner.id),
				);
			},
		});
	};

	return (
		<form>
			<Grid2 container>
				<Grid2 size={{ xs: 12 }} display="flex" flexDirection="column" gap={3}>
					<BaseCard title={t(partnerCreateResources.generalInfo.title)}>
						<Grid2 container spacing={2} mb={3}>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									id="first-name"
									name="partner.name"
									label={t(partnerCreateResources.generalInfo.name)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									id="middle-name"
									name="partner.middleName"
									label={t(partnerCreateResources.generalInfo.middleName)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									id="surname"
									name="partner.surname"
									label={t(partnerCreateResources.generalInfo.surname)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									id="phone"
									name="partner.phone"
									label={t(partnerCreateResources.generalInfo.phone)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									id="email"
									name="partner.email"
									label={t(partnerCreateResources.generalInfo.email)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Checkbox
									id="can-train-days"
									name="partner.isCanTrainDay"
									label={t(partnerCreateResources.generalInfo.isCanTrainDay)}
									control={control}
									errors={errors}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Select
									id="marketId"
									label={t(partnerCreateResources.generalInfo.market)}
									fullWidth
									options={
										marketList?.map((m) => {
											return {
												label: m.name,
												value: m.id,
											};
										}) ?? []
									}
									variant="outlined"
									name="partner.marketId"
									control={control}
									errors={errors}
									numeric
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Select
									id="marketRegionId"
									label={t(partnerCreateResources.generalInfo.marketRegion)}
									fullWidth
									options={
										marketRegionList?.map((mr) => {
											return {
												label: mr.name,
												value: mr.id,
											};
										}) ?? []
									}
									variant="outlined"
									name="partner.marketRegionId"
									control={control}
									errors={errors}
									disabled={!selectedMarketId || isLoadingMarketRegions}
									isLoading={isLoadingMarketRegions}
									numeric
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Select
									id="verticals"
									label={t(partnerCreateResources.generalInfo.verticals)}
									fullWidth
									options={
										verticals?.map((vertical) => ({
											label:
												vertical.name.charAt(0).toUpperCase() +
												vertical.name.slice(1),
											value: vertical.id,
										})) ?? []
									}
									variant="outlined"
									name="partner.verticals"
									control={control}
									errors={errors}
									multiple
									disabled={isLoadingVerticals}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Input
									isNumber
									id="team-size"
									name="partner.teamSize"
									label={t(partnerCreateResources.generalInfo.teamSize)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
								<Select
									id="skills"
									label={t(partnerCreateResources.generalInfo.skills)}
									fullWidth
									options={createSelectOptions(
										PartnerSkills,
										t,
										selectOptionsResource.skills,
									)}
									variant="outlined"
									name="partner.skills"
									control={control}
									errors={errors}
									multiple
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Input
									id="notes"
									name="partner.notes"
									label={t(partnerCreateResources.generalInfo.notes)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
									multiline
									rows={3}
									maxRows={6}
								/>
							</Grid2>
						</Grid2>
					</BaseCard>

					{/* Address */}
					<BaseCard title={t(partnerCreateResources.addressInfo.title)}>
						<Grid2 container spacing={2} mb={3}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="street"
									name="address.street"
									label={t(partnerCreateResources.addressInfo.street)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="city"
									name="address.city"
									label={t(partnerCreateResources.addressInfo.city)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="building-number"
									name="address.buildingNumber"
									label={t(partnerCreateResources.addressInfo.builduingNumber)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="post-code"
									name="address.postCode"
									label={t(partnerCreateResources.addressInfo.postCode)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Select
									id="country"
									label={t(partnerCreateResources.addressInfo.country)}
									fullWidth
									options={createSelectOptions(CountryEnum)}
									variant="outlined"
									name="address.country"
									control={control}
									errors={errors}
								/>
							</Grid2>
						</Grid2>
					</BaseCard>

					{/* Billing profile*/}
					<BaseCard title={t(partnerCreateResources.billingProfile.title)}>
						<Grid2 container spacing={2} mb={3}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Checkbox
									id="vat-liable"
									name="billingProfile.vatLiable"
									label={t(partnerCreateResources.billingProfile.vatLiable)}
									control={control}
									errors={errors}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Checkbox
									id="isSameAddress"
									name="isSameAddress"
									label="Billing address is the same as primary address"
									control={control}
									errors={errors}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="vat-id"
									name="billingProfile.vatId"
									label={t(partnerCreateResources.billingProfile.vatId)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="invoicing-name"
									name="billingProfile.invoicingName"
									label={t(partnerCreateResources.billingProfile.invoicingName)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="business-id"
									name="billingProfile.businessId"
									label={t(partnerCreateResources.billingProfile.businessId)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Select
									id="type"
									name="billingProfile.type"
									label="Type"
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
									options={[
										{ label: "Individual", value: "individual" },
										{ label: "Company", value: "company" },
									]}
								/>
							</Grid2>

							{selectedType === "individual" && (
								<>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="birth-country"
											name="billingProfile.birthCountry"
											label="Birth Country"
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="birth-city"
											name="billingProfile.birthCity"
											label="Birth City"
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<DatePicker
											name="billingProfile.birthDate"
											label="Birth Date"
											control={control}
											errors={errors}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="tin"
											name="billingProfile.tin"
											label="TIN"
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
								</>
							)}

							{!selectedSameAddress && (
								<>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="billing-street"
											name="billingAddress.street"
											label={t(partnerCreateResources.addressInfo.street)}
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="billing-city"
											name="billingAddress.city"
											label={t(partnerCreateResources.addressInfo.city)}
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="billing-building-number"
											name="billingAddress.buildingNumber"
											label={t(
												partnerCreateResources.addressInfo.builduingNumber,
											)}
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Input
											id="billing-post-code"
											name="billingAddress.postCode"
											label={t(partnerCreateResources.addressInfo.postCode)}
											variant="outlined"
											control={control}
											errors={errors}
											fullWidth
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Select
											id="billing-country"
											name="billingAddress.country"
											label={t(partnerCreateResources.addressInfo.country)}
											fullWidth
											options={createSelectOptions(CountryEnum)}
											variant="outlined"
											control={control}
											errors={errors}
										/>
									</Grid2>
								</>
							)}
						</Grid2>
					</BaseCard>

					{/* Bank Account */}
					<BaseCard title={t(partnerCreateResources.bankAccount.title)}>
						<Grid2 container spacing={2} mb={3}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="iban"
									name="bankAccount.iban"
									label={t(partnerCreateResources.bankAccount.iban)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="bic"
									name="bankAccount.bic"
									label={t(partnerCreateResources.bankAccount.bic)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Input
									id="bank-name"
									name="bankAccount.accountHolder"
									label={t(partnerCreateResources.bankAccount.accountHolder)}
									variant="outlined"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Grid2>
						</Grid2>
					</BaseCard>

					<Button
						variant="contained"
						color="primary"
						onClick={handleSubmitCreate}
						sx={{ alignSelf: "flex-start" }}
					>
						{t(partnerCreateResources.create.save)}
					</Button>
				</Grid2>
			</Grid2>
		</form>
	);
};
